/**
 * @file Admin /settings panel route contracts.
 * @module @labcraft/admin/shared/settings-routes
 *
 * The admin daemon owns the operator-facing surface for every
 * daemon's runtime config — schema discovery, current values,
 * per-key history, and the write path. The four routes below are
 * the admin SPA's only entry points into the settings system; each
 * daemon's settings declarations are imported by admin at build
 * time (see `@labcraft/account/shared/settings`), so the schema
 * route doesn't require any of the underlying daemons to be
 * running.
 *
 * All four routes are gated on the admin session cookie
 * (`lc_admin_session`); 401 for anonymous, 200 for authenticated
 * operators. The `setBy` UUID on writes is extracted from the admin
 * session server-side — clients never supply it.
 */

import { z } from 'zod'
import { defineRoute } from '@labcraft/core'
import { SettingDefinitionSchema } from '@labcraft/postgres/shared'

////////////////////////////////////////////////////////////////////////////////
// GET /api/settings/schema
////////////////////////////////////////////////////////////////////////////////

/**
 * Per-daemon group in the schema response. Iteration order across
 * the `daemons` array matches admin's registry; iteration within
 * `keys` matches each daemon's declared order. The UI renders one
 * sub-panel per daemon.
 *
 * @property id - Daemon identifier matching the source workspace
 *   (`account`, `email`, `vmm`, …). Stable; used as the URL
 *   fragment in `/settings/<id>`.
 * @property label - Human-readable header for the sub-panel.
 * @property keys - Settings declarations imported from the daemon's
 *   `shared/settings` module.
 */
const SettingsSchemaGroupSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  keys: z.array(SettingDefinitionSchema),
})

/**
 * Response of `GET /api/settings/schema`. Grouped by daemon so the
 * UI can build sub-panels without re-walking the flat key list.
 */
export const SettingsSchemaResponseSchema = z.object({
  daemons: z.array(SettingsSchemaGroupSchema),
})

/** Parsed `SettingsSchemaResponse`. */
export type SettingsSchemaResponse = z.infer<
  typeof SettingsSchemaResponseSchema
>

/** `GET /api/settings/schema` — declared keys grouped by daemon. */
export const SettingsSchemaRoute = defineRoute({
  method: 'GET',
  path: '/api/settings/schema',
  response: SettingsSchemaResponseSchema,
  description: 'List declared settings grouped by daemon.',
})

////////////////////////////////////////////////////////////////////////////////
// GET /api/settings/values
////////////////////////////////////////////////////////////////////////////////

/**
 * Response of `GET /api/settings/values`. Nested
 * `daemon → key → value` map; keys with no history row fall back to
 * their declared default. Nested (not flat) because keys are unique
 * only within a daemon's namespace (#875) — `auth_session_ttl` exists
 * under `admin`, `lab`, and `www` with independent values.
 */
export const SettingsValuesResponseSchema = z.object({
  values: z.record(z.string(), z.record(z.string(), z.unknown())),
})

/** Parsed `SettingsValuesResponse`. */
export type SettingsValuesResponse = z.infer<
  typeof SettingsValuesResponseSchema
>

/** `GET /api/settings/values` — current value of every declared key. */
export const SettingsValuesRoute = defineRoute({
  method: 'GET',
  path: '/api/settings/values',
  response: SettingsValuesResponseSchema,
  description: 'Latest value of every declared key.',
})

////////////////////////////////////////////////////////////////////////////////
// GET /api/settings/:key/history
////////////////////////////////////////////////////////////////////////////////

/** One row of `system_settings` history. */
const SettingHistoryEntrySchema = z.object({
  value: z.unknown(),
  setAt: z.string().datetime(),
  setBy: z.string().uuid(),
})

/**
 * Response of `GET /api/settings/:key/history`. Newest-first list of
 * every value the key has ever held.
 */
export const SettingsHistoryResponseSchema = z.object({
  history: z.array(SettingHistoryEntrySchema),
})

/** Parsed `SettingsHistoryResponse`. */
export type SettingsHistoryResponse = z.infer<
  typeof SettingsHistoryResponseSchema
>

/**
 * Params for `GET /api/settings/:daemon/:key/history` and `PUT
 * /api/settings/:daemon/:key`. The `(daemon, key)` pair is the storage
 * identity post-#875 — a bare key is ambiguous across daemons. Both
 * are non-empty strings; the wire shape is permissive so adding new
 * daemons / keys never needs a route-level enum bump.
 */
export const SettingsKeyParamsSchema = z.object({
  daemon: z.string().min(1),
  key: z.string().min(1),
})

/**
 * `GET /api/settings/:daemon/:key/history` — full append-only history
 * for one (daemon, key).
 */
export const SettingsHistoryRoute = defineRoute({
  method: 'GET',
  path: '/api/settings/:daemon/:key/history',
  params: SettingsKeyParamsSchema,
  response: SettingsHistoryResponseSchema,
  description: 'Full append-only history of one daemon-scoped settings key.',
})

////////////////////////////////////////////////////////////////////////////////
// PUT /api/settings/:key
////////////////////////////////////////////////////////////////////////////////

/**
 * Request body of `PUT /api/settings/:daemon/:key`. The operator UUID
 * is NOT in the body — the admin daemon extracts it from the session
 * cookie at the handler. Client supplies only the new value.
 */
export const SetSettingRequestSchema = z.object({
  value: z.unknown(),
})

/** Parsed `SetSettingRequest`. */
export type SetSettingRequest = z.infer<typeof SetSettingRequestSchema>

/**
 * Response of `PUT /api/settings/:daemon/:key`. Returns the row that
 * was just inserted so the UI can re-render without a follow-up GET.
 */
export const SetSettingResponseSchema = z.object({
  entry: SettingHistoryEntrySchema,
})

/** Parsed `SetSettingResponse`. */
export type SetSettingResponse = z.infer<typeof SetSettingResponseSchema>

/**
 * `PUT /api/settings/:daemon/:key` — write a new value for one
 * daemon-scoped settings key (append-only).
 */
export const SetSettingRoute = defineRoute({
  method: 'PUT',
  path: '/api/settings/:daemon/:key',
  params: SettingsKeyParamsSchema,
  request: SetSettingRequestSchema,
  response: SetSettingResponseSchema,
  description: 'Write a new value for one daemon-scoped settings key.',
})
